import { X, Download, Loader2, FileX, Trash, Eye, Pencil } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { jsPDF } from 'jspdf';
import { useState, useEffect } from 'react';
import {
  getContractAnalyses,
  getContractReport,
  deleteContractAnalysis,
  getContractAnalysisById,
  updateContractAnalysis,
} from '../../api/api';
import { clearReport } from '../../redux/slices/reportSlice';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import MDEditor from '@uiw/react-md-editor';

export default function RightSidebar({
  isOpen,
  onClose,
  isDarkMode,
  children,
}) {
  const report = useSelector((state) => state.report.report);

  const { user } = useSelector((state) => state.auth);
  const uid = user?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('reports');
  const [history, setHistory] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [editorResults, setEditorResults] = useState({});
  const [analysisType, setAnalysisType] = useState('');
  const [question, setQuestion] = useState('');
  const [activeSectionKeys, setActiveSectionKeys] = useState([]);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const dispatch = useDispatch();
  const SECTIONS = [
    { key: 'risk_analysis', label: 'Risk Analysis' },
    { key: 'key_clauses', label: 'Key Clauses' },
    { key: 'compliance_check', label: 'Compliance Check' },
    { key: 'research', label: 'Research Findings' },
    { key: 'qa_response', label: 'Questions & Answers' },
  ];
  const fetchHistory = async () => {
    if (!uid) return;

    try {
      setLoadingHistory(true);
      const res = await getContractAnalyses(uid);
      setHistory(res.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setIsModalLoading(false);
    setSelectedAnalysis(null);
    setEditorResults({});
    setActiveSectionKeys([]);
    setAnalysisType('');
    setQuestion('');
    setConfirmSaveOpen(false);
  };

  const openAnalysisModal = async (analysisId, edit = false) => {
    if (isModalLoading) return;

    setIsModalOpen(true);
    setIsEditMode(edit);
    setIsModalLoading(true);

    setSelectedAnalysis(null);
    setEditorResults({});
    setActiveSectionKeys([]);

    try {
      const res = await getContractAnalysisById(analysisId, uid);

      if (!res?.report) {
        toast.error('Invalid analysis response');
        closeModal();
        return;
      }

      const report = res.report;
      const results = report.analysis_results || {};

      setSelectedAnalysis(report);
      setEditorResults(results);
      setActiveSectionKeys(Object.keys(results));
      setAnalysisType(report.analysis_type);
      setQuestion(report.question || '');
    } catch (err) {
      toast.error('Failed to load analysis');
      closeModal();
    } finally {
      setIsModalLoading(false); // 👈 content is ready
    }
  };

  const handleSaveAnalysis = async () => {
    if (!selectedAnalysis) return;

    try {
      setIsSaving(true);

      await updateContractAnalysis({
        analysisId: selectedAnalysis.analysis_id,
        uid,
        results: editorResults,
        analysis_type: analysisType,
        question,
      });

      toast.success('Analysis updated');

      closeModal();

      await fetchHistory();
      // ✅ move user to history (this triggers fetchHistory)
      setActiveTab('history');
    } catch {
      toast.error('Failed to update analysis');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [uid, activeTab]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('reports'); // reset on close
    }
  }, [isOpen]);

  // Helper to format the title from analysis_type
  const formatTitle = (text) => {
    if (!text) return ' Report';
    return (
      text
        .replace(/_/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') + ' Report'
    );
  };

  useEffect(() => {
    if (report) {
      setIsLoading(true);
      const timeout = setTimeout(() => setIsLoading(false), 500);
      return () => clearTimeout(timeout);
    }
  }, [report]);

  const stripMarkdown = (md) => {
    if (!md) return '';
    return md
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(\*|_)(.*?)\1/g, '$2')
      .replace(/(#+)\s*(.*)/g, '$2')
      .replace(/^-+\s*/gm, '')
      .replace(/^\s*>\s?/gm, '')
      .replace(/\r?\n|\r/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const handleDownload = () => {
    try {
      if (!report || !report.contract_name) {
        console.error('No valid report found to download.');
        return;
      }

      const doc = new jsPDF();

      const title = formatTitle(report.analysis_type);
      doc.setFontSize(18);
      doc.text(title, 10, 15);

      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Contract Name: ${report.contract_name}`, 10, 25);
      doc.text(
        `Analysis Date: ${new Date(report.analysis_date).toLocaleString()}`,
        10,
        32
      );
      doc.text(`Analysis Type: ${report.analysis_type}`, 10, 39);

      // Divider
      doc.setDrawColor(0);
      doc.line(10, 43, 200, 43);

      // Sections

      let y = 50;
      const lineHeight = 7;
      const pageHeight = doc.internal.pageSize.height;

      SECTIONS.forEach(({ key, label }) => {
        const value = report.analysis_results?.[key];
        if (!value) return;

        doc.setFont(undefined, 'bold');
        doc.setFontSize(14);
        doc.text(label, 10, y);
        y += lineHeight;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(12);
        const splitText = doc.splitTextToSize(stripMarkdown(value), 180);

        splitText.forEach((line) => {
          if (y + lineHeight > pageHeight - 10) {
            doc.addPage();
            y = 20;
          }
          doc.text(line, 10, y);
          y += lineHeight;
        });

        y += lineHeight;
      });

      const name = formatTitle(report.analysis_type);
      doc.save(`${name.replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      console.error('Error generating report PDF:', err);
    }
  };

  const handleHistoryDownload = async (item) => {
    try {
      if (!item?.contract_name) {
        console.error('No contract name found for history item.');
        return;
      }

      setIsLoading(true);

      // ✅ Fetch full report JSON from backend
      const res = await getContractReport(uid, item.contract_name);

      if (!res?.report) {
        console.error('No report data returned from API.');
        return;
      }

      const report = res.report; // 👈 use the nested report object

      const doc = new jsPDF();
      const title = formatTitle(report.analysis_type || item.analysis_type);

      // Title
      doc.setFontSize(18);
      doc.text(title, 10, 15);

      // Contract Info
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Contract Name: ${report.contract_name}`, 10, 25);
      doc.text(
        `Analysis Date: ${new Date(report.analysis_date).toLocaleString()}`,
        10,
        32
      );
      doc.text(`Analysis Type: ${report.analysis_type}`, 10, 39);

      // Divider
      doc.setDrawColor(0);
      doc.line(10, 43, 200, 43);

      // Sections
      const sections = [
        { key: 'risk_analysis', label: 'Risk Analysis' },
        { key: 'key_clauses', label: 'Key Clauses' },
        { key: 'compliance_check', label: 'Compliance Check' },
        { key: 'research', label: 'Research Findings' },
        { key: 'qa_response', label: 'Questions & Answers' },
      ];

      let y = 50;
      const lineHeight = 7;
      const pageHeight = doc.internal.pageSize.height;

      sections.forEach(({ key, label }) => {
        const value = report.analysis_results?.[key];
        if (!value) return;

        doc.setFont(undefined, 'bold');
        doc.setFontSize(14);
        doc.text(label, 10, y);
        y += lineHeight;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(12);

        const splitText = doc.splitTextToSize(stripMarkdown(value), 180);

        splitText.forEach((line) => {
          if (y + lineHeight > pageHeight - 10) {
            doc.addPage();
            y = 20;
          }
          doc.text(line, 10, y);
          y += lineHeight;
        });

        y += lineHeight;
      });

      const fileName =
        `${report.contract_name}_${report.analysis_type}_Report.pdf`.replace(
          /\s+/g,
          '_'
        );
      doc.save(fileName);
    } catch (err) {
      console.error('Error downloading history report:', err);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteAnalysis = async () => {
    if (!deleteTarget) return;

    try {
      setIsLoading(true);
      let result = await deleteContractAnalysis(deleteTarget, uid);
      toast.success(result?.message || 'Deleted successfully');
      setHistory((prev) => prev.filter((a) => a.analysis_id !== deleteTarget));
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete');
    } finally {
      setIsLoading(false);
      setDeleteTarget(null);
    }
  };
  const visibleSections = SECTIONS.filter(({ key }) => {
    if (!activeSectionKeys.includes(key)) return false;
    if (isEditMode) return true;
    return editorResults[key]?.trim();
  });

  const isAnalysisReady =
    !isModalLoading &&
    !!selectedAnalysis &&
    Object.keys(editorResults).length > 0;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay (click outside to close) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.15 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/10 backdrop-blur-sm z-40"
              onClick={onClose}
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 120, damping: 18 }}
              className={`fixed right-0 top-0 h-full w-80 shadow-xl z-50 border-l flex flex-col
            ${
              isDarkMode
                ? 'bg-gray-900 border-gray-700 text-white'
                : 'bg-white border-gray-200 text-black'
            }`}
            >
              {/* Header */}
              <div className="flex items-center justify-end p-4 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-brand hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveTab('reports')}
                  className={`flex-1 py-2 text-sm font-medium ${
                    activeTab === 'reports'
                      ? 'border-b-2 border-brand text-brand'
                      : 'text-gray-500'
                  }`}
                >
                  Reports
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`flex-1 py-2 text-sm font-medium ${
                    activeTab === 'history'
                      ? 'border-b-2 border-brand text-brand'
                      : 'text-gray-500'
                  }`}
                >
                  History
                </button>
              </div>

              {/* Body */}
              <div className="p-4 text-sm space-y-4 overflow-y-auto h-[calc(100%-100px)]">
                {activeTab === 'reports' && (
                  <>
                    {isLoading ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading report...
                      </div>
                    ) : report ? (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`font-medium ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            Reports
                          </h3>

                          <button
                            onClick={() => dispatch(clearReport())}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                          >
                            <X size={12} />
                            Clear All
                          </button>
                        </div>
                        <div
                          className={`flex items-center justify-between rounded-lg p-3 shadow-sm ${
                            isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                          }`}
                        >
                          <div>
                            <p
                              className={`text-sm font-medium ${
                                isDarkMode ? 'text-white' : 'text-gray-800'
                              }`}
                            >
                              {formatTitle(report.analysis_type)}
                            </p>
                            <p className="text-xs text-gray-400">
                              {report.analysis_date &&
                              !isNaN(new Date(report.analysis_date))
                                ? new Date(report.analysis_date)
                                    .toISOString()
                                    .split('T')[0]
                                : 'N/A'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {/* Preview */}
                            <button
                              onClick={() => {
                                if (!report?.analysis_id) {
                                  toast.error(
                                    'This report cannot be previewed yet'
                                  );
                                  return;
                                }
                                openAnalysisModal(report.analysis_id, false);
                              }}
                              className="p-2 rounded bg-gray-500 text-white hover:bg-gray-600 transition"
                              title="Preview"
                            >
                              <Eye size={16} />
                            </button>

                            {/* Edit */}
                            <button
                              onClick={() => {
                                if (!report?.analysis_id) {
                                  toast.error(
                                    'This report cannot be edited yet'
                                  );
                                  return;
                                }
                                openAnalysisModal(report.analysis_id, true);
                              }}
                              className="p-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>

                            {/* Download */}
                            <button
                              onClick={handleDownload}
                              className="p-2 rounded bg-brand text-white hover:bg-brandDark transition"
                              title="Download"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`flex flex-col items-center justify-center text-center p-4 rounded-lg border border-dashed ${
                          isDarkMode
                            ? 'border-brandLight text-brandLight'
                            : 'border-brand text-brand'
                        }`}
                      >
                        <FileX
                          className={`w-8 h-8 mb-2 ${
                            isDarkMode ? 'text-brandLight' : 'text-brand'
                          }`}
                        />
                        <p
                          className={`text-sm font-medium ${
                            isDarkMode ? 'text-brandLight' : 'text-brandDark'
                          }`}
                        >
                          No report available
                        </p>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'history' && (
                  <div>
                    {loadingHistory ? (
                      <div
                        className={`flex flex-col items-center justify-center text-center p-4 rounded-lg border border-dashed ${
                          isDarkMode
                            ? 'border-brandLight text-brandLight'
                            : 'border-brand text-brand'
                        }`}
                      >
                        <Loader2
                          className={`w-6 h-6 mb-2 animate-spin ${
                            isDarkMode ? 'text-brandLight' : 'text-brand'
                          }`}
                        />
                        <p
                          className={`text-sm font-medium ${
                            isDarkMode ? 'text-brandLight' : 'text-brandDark'
                          }`}
                        >
                          Loading history...
                        </p>
                      </div>
                    ) : history.length === 0 ? (
                      <div
                        className={`flex flex-col items-center justify-center text-center p-4 rounded-lg border border-dashed ${
                          isDarkMode
                            ? 'border-brandLight text-brandLight'
                            : 'border-brand text-brand'
                        }`}
                      >
                        <FileX
                          className={`w-8 h-8 mb-2 ${
                            isDarkMode ? 'text-brandLight' : 'text-brand'
                          }`}
                        />
                        <p
                          className={`text-sm font-medium ${
                            isDarkMode ? 'text-brandLight' : 'text-brandDark'
                          }`}
                        >
                          No history available
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {history.map((item, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg shadow-sm space-y-3 ${
                              isDarkMode
                                ? 'bg-gray-700 text-white'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {/* Content */}
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">
                                {formatTitle(item.analysis_type)}
                              </p>

                              <p className="text-xs text-gray-400">
                                {new Date(
                                  item.analysis_date
                                ).toLocaleDateString()}
                              </p>

                              <p className="text-xs mt-1 line-clamp-2">
                                {item.content_preview}
                              </p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 mx-auto justify-center">
                              {/* Preview */}
                              <div className="relative group">
                                <button
                                  onClick={() =>
                                    openAnalysisModal(item.analysis_id, false)
                                  }
                                  className="p-2 rounded bg-gray-500 text-white hover:bg-gray-600 transition"
                                >
                                  <Eye size={16} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                                  Preview
                                </span>
                              </div>

                              {/* Edit */}
                              <div className="relative group">
                                <button
                                  onClick={() =>
                                    openAnalysisModal(item.analysis_id, true)
                                  }
                                  className="p-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition"
                                >
                                  <Pencil size={16} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                                  Edit
                                </span>
                              </div>

                              {/* Download */}
                              <div className="relative group">
                                <button
                                  onClick={() => handleHistoryDownload(item)}
                                  className="p-2 rounded bg-brand text-white hover:bg-brandDark transition"
                                >
                                  <Download size={16} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                                  Download
                                </span>
                              </div>

                              {/* Delete */}
                              <div className="relative group">
                                <button
                                  onClick={() =>
                                    setDeleteTarget(item.analysis_id)
                                  }
                                  className="p-2 rounded bg-red-500 text-white hover:bg-red-600 transition"
                                >
                                  <Trash size={16} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                                  Delete
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {children}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            key="delete-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className={`w-full max-w-sm p-6 sm:p-7 rounded-2xl shadow-2xl transition-colors duration-300 border
          ${
            isDarkMode
              ? 'bg-gray-900 text-gray-100 border-gray-700'
              : 'bg-white text-gray-900 border-gray-200'
          }
        `}
            >
              {/* Header */}
              <h2
                className={`text-lg sm:text-xl font-semibold text-center mb-3 ${
                  isDarkMode ? 'text-red-400' : 'text-red-600'
                }`}
              >
                Confirm Deletion
              </h2>

              {/* Message */}
              <p
                className={`text-sm text-center leading-relaxed mb-6 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                Are you sure you want to delete this analysis?
                <br />
                This action{' '}
                <span className="font-medium text-red-500">
                  cannot be undone.
                </span>
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-3 mt-4">
                {/* Cancel Button */}
                <button
                  onClick={() => !isLoading && setDeleteTarget(null)}
                  disabled={isLoading}
                  className={`flex-1 py-2 text-sm font-medium rounded-md border transition-colors duration-200 text-center
      ${
        isDarkMode
          ? 'border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50'
          : 'border-gray-400 text-gray-700 hover:bg-gray-100 disabled:opacity-50'
      }`}
                >
                  Cancel
                </button>

                {/* Delete Button */}
                <button
                  onClick={handleDeleteAnalysis}
                  disabled={isLoading}
                  className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all duration-200 text-center
      ${
        isLoading
          ? 'bg-red-700 opacity-80 cursor-not-allowed text-white'
          : 'bg-[#E22D2D] hover:bg-[#C92424] text-white shadow-sm'
      }`}
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
            onClick={() => {
              if (!isEditMode) closeModal();
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={`w-full max-w-5xl h-[90vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl border
    ${
      isDarkMode
        ? 'bg-gray-900 border-gray-700 text-gray-100'
        : 'bg-gradient-to-br from-white via-gray-50 to-gray-100 border-gray-200 text-gray-900'
    }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-center justify-between px-6 py-4
  border-b sticky top-0 z-10 backdrop-blur-md
  ${
    isDarkMode
      ? 'bg-gray-900/80 border-gray-700'
      : 'bg-white/70 border-gray-200'
  }`}
              >
                <div>
                  <h2
                    className={`text-xl font-semibold ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}
                  >
                    {formatTitle(analysisType)}
                  </h2>

                  <p
                    className={`mt-1 text-sm flex items-center gap-2
        ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
      `}
                  >
                    {isEditMode && (
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                    )}
                    {isEditMode ? 'Edit mode — changes will be saved' : ''}
                  </p>
                </div>

                <button
                  onClick={closeModal}
                  disabled={!isAnalysisReady}
                  className="p-2 rounded-full hover:bg-gray-100 transition"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {isModalLoading ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-3">
                    <Loader2 className="w-10 h-10 animate-spin text-brand opacity-80" />
                  </div>
                ) : (
                  <>
                    {visibleSections.map(({ key, label }) => (
                      <div
                        key={key}
                        className={`mb-8 rounded-xl border shadow-sm
${
  isDarkMode
    ? isEditMode
      ? 'bg-gray-800 border-gray-700 shadow-sm'
      : 'bg-gray-900/60 border-gray-800 shadow-none'
    : 'bg-white border-gray-200'
}`}
                      >
                        <div
                          className={`px-5 py-3 border-b rounded-t-xl ${
                            isDarkMode
                              ? 'bg-gray-900 border-gray-700'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <h3
                            className={`font-semibold ${
                              isDarkMode ? 'text-gray-100' : 'text-gray-800'
                            }`}
                          >
                            {label}
                          </h3>
                        </div>

                        <div className="p-5">
                          {isEditMode ? (
                            <div
                              data-color-mode={isDarkMode ? 'dark' : 'light'}
                              className="h-full flex flex-col"
                            >
                              <MDEditor
                                value={editorResults[key] || ''}
                                onChange={(val) =>
                                  setEditorResults((prev) => ({
                                    ...prev,
                                    [key]: val,
                                  }))
                                }
                                height="100%"
                              />
                            </div>
                          ) : (
                            <div
                              data-color-mode={isDarkMode ? 'dark' : 'light'}
                              className="prose max-w-none prose-gray"
                            >
                              <MDEditor.Markdown
                                source={editorResults[key] || ''}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
              {isEditMode && (
                <div
                  className={`sticky bottom-0 backdrop-blur-md border-t px-6 py-4 flex justify-end gap-3
  ${
    isDarkMode
      ? 'bg-gray-900/80 border-gray-700'
      : 'bg-white/80 border-gray-200'
  }`}
                >
                  <button
                    onClick={closeModal}
                    disabled={!isAnalysisReady}
                    className={`px-4 py-2 rounded-md border transition
    ${
      !isAnalysisReady
        ? 'opacity-50 cursor-not-allowed'
        : isDarkMode
        ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
        : 'border-gray-300 text-gray-600 hover:bg-gray-100'
    }
  `}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => setConfirmSaveOpen(true)}
                    disabled={!isAnalysisReady || isSaving}
                    className={`px-6 py-2 rounded-md font-semibold transition
    ${
      !isAnalysisReady || isSaving
        ? 'bg-gray-400 cursor-not-allowed text-white'
        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
    }
  `}
                  >
                    {isSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {confirmSaveOpen && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className={`w-full max-w-md p-6 rounded-2xl shadow-2xl border
          ${
            isDarkMode
              ? 'bg-gray-900 text-gray-100 border-gray-700'
              : 'bg-white text-gray-900 border-gray-200'
          }
        `}
            >
              <h2 className="text-lg font-semibold mb-2">
                Confirm save changes
              </h2>

              <p className="text-sm opacity-80 mb-6 leading-relaxed">
                You are about to permanently update this contract analysis.
                <br />
                Are you sure you want to continue?
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmSaveOpen(false)}
                  className={`px-4 py-2 rounded-md border transition
              ${
                isDarkMode
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-100'
              }
            `}
                >
                  Cancel
                </button>

                <button
                  onClick={async () => {
                    if (!isAnalysisReady) return;
                    setConfirmSaveOpen(false);
                    await handleSaveAnalysis();
                  }}
                  disabled={!isAnalysisReady || isSaving}
                  className={`px-4 py-2 rounded-md font-semibold transition
    ${
      !isAnalysisReady || isSaving
        ? 'bg-gray-400 cursor-not-allowed text-white'
        : 'bg-green-600 hover:bg-green-700 text-white'
    }
  `}
                >
                  Yes, save changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
